import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  RiArrowLeftLine, RiCheckLine, RiLockLine, RiPlayFill,
  RiLightbulbLine, RiBookOpenLine, RiArrowDownSLine,
} from 'react-icons/ri';
import { C } from '../../data/constants';
import { getGrammarChapter, completeGrammarPart } from '../../api/grammar.api';
import { getStreakCalendar } from '../../api/quiz.api';
import { LoadingScreen } from '../../components/shared/UI';
import { useAuth } from '../../context/AuthContext';
import { isStudiedToday } from '../../utils/streak';
import StreakCelebration from '../../components/app/StreakCelebration';
import { useAppLevel } from '../../components/app/AppTopbar';
import GrammarLesson from './GrammarLesson';

/* ══════════════════════════════════════════════════════════════
   CHAPTER PAGE
   The briefing comes first and the drills come second, deliberately:
   the intro is where "what is は and why do we use it" gets answered,
   and every part below assumes you have read it.
══════════════════════════════════════════════════════════════ */

export default function GrammarChapter() {
  const { number } = useParams();
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const { level } = useAppLevel();

  const [chapter, setChapter] = useState(null);
  const [error, setError] = useState('');
  const [activePart, setActivePart] = useState(null);
  const [streakDoneToday, setStreakDoneToday] = useState(null);
  const [celebration, setCelebration] = useState(null);

  /* The briefing is collapsed to its key points by default. On a phone the
     full prose was ~900px of reading before the first lesson was even on
     screen, and most of that is nuance a returning learner has already
     read. Opening it is remembered per chapter, so anyone who prefers the
     long version only has to say so once. */
  const introKey = `kitsu-grammar-intro-${number}`;
  const [introOpen, setIntroOpen] = useState(() => {
    try { return localStorage.getItem(introKey) === 'open'; }
    catch (_) { return false; }
  });

  const toggleIntro = useCallback(() => {
    setIntroOpen(open => {
      const next = !open;
      try { localStorage.setItem(introKey, next ? 'open' : 'closed'); } catch (_) {}
      return next;
    });
  }, [introKey]);

  const load = useCallback(() => {
    setError('');
    getGrammarChapter(number, level)
      .then(res => setChapter(res.data.chapter))
      .catch(err => setError(err.message));
  }, [number, level]);

  useEffect(() => { load(); }, [load]);

  // Was today already earned before this visit? Decides whether finishing a
  // part is worth a celebration screen.
  useEffect(() => {
    let alive = true;
    getStreakCalendar()
      .then(res => { if (alive) setStreakDoneToday(isStudiedToday(res.data.lastStudied)); })
      .catch(() => { if (alive) setStreakDoneToday(true); });
    return () => { alive = false; };
  }, []);

  // The runner takes over the screen, so the page behind it must not scroll.
  useEffect(() => {
    if (!activePart) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [activePart]);

  const handleFinished = useCallback(scorePct => {
    completeGrammarPart({ chapter: Number(number), part: activePart.index, scorePct, level })
      .then(() => {
        load();
        if (streakDoneToday === false) {
          setStreakDoneToday(true);
          return getStreakCalendar().then(res => {
            setCelebration({ streak: res.data.streak, lastStudied: res.data.lastStudied });
            updateUser({ streak: res.data.streak, lastStudied: res.data.lastStudied });
          });
        }
        return null;
      })
      .catch(() => {});
  }, [number, level, activePart, load, streakDoneToday, updateUser]);

  if (error && !chapter) {
    return (
      <div className="page-container" style={{ textAlign: 'center', padding: 40 }}>
        <div style={{ color: C.red, fontSize: 13, marginBottom: 16 }}>{error}</div>
        <button className="btn-ghost" onClick={() => navigate('/grammar')}>Back to chapters</button>
      </div>
    );
  }

  if (!chapter) return <LoadingScreen label="Loading chapter" />;

  if (chapter.locked) {
    return (
      <div className="page-container gr-chapter-page anim-fade-up">
        <button className="gr-back" onClick={() => navigate('/grammar')}>
          <RiArrowLeftLine size={14} /> All chapters
        </button>
        <div className="ks-card" style={{ textAlign: 'center', padding: 40 }}>
          <RiLockLine size={30} style={{ color: C.muted, marginBottom: 12 }} />
          <h2 style={{ fontSize: 18, fontWeight: 900, marginBottom: 6 }}>Chapter {chapter.number} is locked</h2>
          <p style={{ color: C.muted, fontSize: 14 }}>
            Finish chapter {chapter.number - 1} first — this one builds directly on it.
          </p>
        </div>
      </div>
    );
  }

  const paragraphs = (chapter.intro || '').split(/\n\s*\n/).filter(Boolean);
  // ~200 wpm, floored at 1 so it never advertises a "0 min" read
  const readMinutes = Math.max(1, Math.round((chapter.intro || '').split(/\s+/).length / 200));

  return (
    <div className="page-container gr-chapter-page anim-fade-up">
      <button className="gr-back" onClick={() => navigate('/grammar')}>
        <RiArrowLeftLine size={14} /> All chapters
      </button>

      <header className="gr-chapter-head" style={{ '--ch-label': `"${chapter.number}"` }}>
        <span className="gr-chapter-badge">Chapter {chapter.number}</span>
        {/* English as the headline here too, so the chapter page reads
            the same way round as the card that opened it. */}
        <h1>{chapter.titleEn}</h1>
        <p className="gr-chapter-title-jp">{chapter.title}</p>
        {chapter.summary && <p className="gr-chapter-summary">{chapter.summary}</p>}
        <div className="gr-chapter-meter">
          <span><i style={{ width: `${chapter.partsTotal ? (chapter.partsDone / chapter.partsTotal) * 100 : 0}%` }} /></span>
          <em>{chapter.partsDone} of {chapter.partsTotal} parts</em>
        </div>
      </header>

      {/* The briefing. Key points are always on show — they are the whole
          explanation in four lines — and the prose behind them is opt-in. */}
      {(paragraphs.length > 0 || chapter.keyPoints?.length > 0) && (
        <section className={`ks-card gr-intro${introOpen ? ' open' : ''}`}>
          <button
            type="button"
            className="gr-intro-head"
            onClick={toggleIntro}
            aria-expanded={introOpen}
            aria-controls={`gr-intro-body-${number}`}
            disabled={paragraphs.length === 0}
          >
            <span className="gr-intro-title">
              <RiBookOpenLine size={16} /> Before you start
            </span>
            {paragraphs.length > 0 && (
              <span className="gr-intro-toggle">
                {introOpen ? 'Show less' : `Read the full explanation · ${readMinutes} min`}
                <RiArrowDownSLine size={17} />
              </span>
            )}
          </button>

          {/* 0fr → 1fr animates a height the browser has to measure; the
              child needs overflow:hidden and min-height:0 for it to work. */}
          <div
            id={`gr-intro-body-${number}`}
            className="gr-intro-collapse"
            style={{ gridTemplateRows: introOpen ? '1fr' : '0fr' }}
            aria-hidden={!introOpen}
          >
            <div>
              <div className="gr-intro-prose">
                {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </div>
          </div>

          {/* Key points are the collapsed summary. Without them a closed
              card would be a bare header, so fall back to a clamped
              first paragraph. */}
          {!introOpen && !chapter.keyPoints?.length && paragraphs.length > 0 && (
            <p className="gr-intro-teaser">{paragraphs[0]}</p>
          )}

          {!!chapter.keyPoints?.length && (
            <ul className="gr-keypoints">
              {chapter.keyPoints.map((k, i) => (
                <li key={i}><RiLightbulbLine size={14} /> <span>{k}</span></li>
              ))}
            </ul>
          )}
        </section>
      )}

      {chapter.parts.length === 0 ? (
        <div className="ks-card" style={{ textAlign: 'center', padding: 34, color: C.muted }}>
          This chapter has not been written yet.
        </div>
      ) : (
        <>
        <div className="gr-parts-head">
          <h2>Parts</h2>
          <span>{chapter.partsTotal} in this chapter</span>
        </div>
        <ol className="gr-parts">
          {chapter.parts.map(part => (
            <li key={part._id} className={`gr-part${part.done ? ' done' : ''}`}>
              <span className="gr-part-marker">
                {part.done ? <RiCheckLine size={15} /> : part.label}
              </span>

              <div className="gr-part-body">
                <div className="gr-part-title">
                  <strong>{part.title}</strong>
                  {part.done && <em>{part.best}%</em>}
                </div>
                {/* Most parts are titled after their own pattern, so showing
                    both just prints the same string twice. */}
                {(part.pattern && part.pattern !== part.title) || part.patternEn ? (
                  <div className="gr-part-pattern">
                    {part.pattern !== part.title && <code>{part.pattern}</code>}
                    {part.patternEn && <span>{part.patternEn}</span>}
                  </div>
                ) : null}
                <span className="gr-part-count">
                  {part.sentences.length} sentence{part.sentences.length === 1 ? '' : 's'}
                </span>
              </div>

              <button
                type="button"
                className={part.done ? 'btn-ghost gr-part-go' : 'btn-primary gr-part-go'}
                disabled={part.sentences.length === 0}
                onClick={() => setActivePart(part)}
              >
                <RiPlayFill size={14} /> {part.done ? 'Review' : 'Start'}
              </button>
            </li>
          ))}
        </ol>
        </>
      )}

      {activePart && createPortal(
        <GrammarLesson
          chapter={chapter}
          part={activePart}
          onExit={() => setActivePart(null)}
          onFinished={handleFinished}
        />,
        document.body,
      )}

      {celebration && (
        <StreakCelebration
          streak={celebration.streak}
          lastStudied={celebration.lastStudied}
          onContinue={() => setCelebration(null)}
        />
      )}
    </div>
  );
}
